import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Send, Loader2, CheckCircle2, Sparkles, Search } from "lucide-react";
import { useMarketingAssets, useAssetSendLog } from "@/hooks/useGrowthMarketing";
import { useReferralLeads } from "@/hooks/useReferralLeads";
import { isValidEmail } from "@/lib/validation";

type Tab = "assets" | "create" | "history";
type AssetType = "brochure" | "flyer" | "case_study" | "one_pager" | "social" | "testimonial";

interface Asset {
  id: string;
  type: AssetType;
  title: string;
  createdAt: string;
}

const ASSET_LABELS: Record<AssetType, string> = {
  brochure: "Tri-fold brochure",
  flyer: "Flyer",
  case_study: "Case study",
  one_pager: "One-pager",
  social: "Social graphic",
  testimonial: "Testimonial card",
};

function AssetPreview({ type }: { type: AssetType }) {
  if (type === "brochure")
    return (
      <div className="aspect-[3/2] grid grid-cols-3 gap-1 bg-cream p-2 border rounded">
        <div className="bg-navy text-cream p-2 text-[10px] flex flex-col justify-between"><span className="font-bold">Compassion at home</span><span>Page 1</span></div>
        <div className="bg-white p-2 text-[10px] text-navy">Services overview</div>
        <div className="bg-gold p-2 text-[10px] text-navy font-bold">Call (555) 123-4567</div>
      </div>
    );
  if (type === "flyer")
    return (
      <div className="aspect-[3/4] bg-cream border-2 border-gold p-3 rounded text-center flex flex-col justify-center">
        <div className="text-navy font-bold text-sm">Need help at home?</div>
        <div className="text-xs text-muted-foreground mt-1">Compassionate care, 24/7</div>
        <div className="mt-3 inline-block bg-gold text-navy px-2 py-1 text-[10px] font-bold rounded">Free consult</div>
      </div>
    );
  if (type === "case_study")
    return (
      <div className="aspect-[3/4] bg-white border p-3 rounded text-[10px]">
        <div className="text-gold font-bold uppercase">Case study</div>
        <div className="text-navy font-bold mt-1">Helping the Johnson family</div>
        <div className="text-muted-foreground mt-2 line-clamp-6">After Mr. Johnson's stroke, his family needed reliable…</div>
      </div>
    );
  if (type === "one_pager")
    return (
      <div className="aspect-[3/4] bg-white border p-3 rounded text-[10px]">
        <div className="bg-navy text-cream p-2 -m-3 mb-2">Service overview</div>
        <div className="space-y-1"><div>• Personal care</div><div>• Memory care</div><div>• Respite</div></div>
      </div>
    );
  if (type === "social")
    return (
      <div className="aspect-square bg-gradient-to-br from-navy to-gold p-4 rounded text-cream flex flex-col justify-center text-center">
        <div className="text-xs font-bold">Did you know?</div>
        <div className="text-sm font-bold mt-1">87% of seniors prefer home care</div>
      </div>
    );
  return (
    <div className="aspect-square bg-cream border-2 border-gold p-3 rounded flex flex-col justify-center text-center">
      <div className="text-navy italic text-xs">"They changed our lives."</div>
      <div className="text-navy font-bold text-[10px] mt-2">— Margaret S.</div>
    </div>
  );
}

export default function MarketingToolkit() {
  const [tab, setTab] = useState<Tab>("assets");
  const { items: assets, add: addAsset, incrementSend, loading: assetsLoading } = useMarketingAssets();
  const { items: history, log: logSend, loading: historyLoading } = useAssetSendLog();
  const { referralLeads } = useReferralLeads();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAsset, setPickerAsset] = useState<{ id: string; title: string; send_count: number } | null>(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [sending, setSending] = useState(false);

  const recipients = referralLeads
    .map((r) => {
      const email = (r.enrichment_data?.email as string | undefined) ?? "";
      return { id: r.id, org_name: r.org_name, contact_name: r.contact_name, email };
    })
    .filter((r) => r.email && isValidEmail(r.email));

  const filteredRecipients = recipients.filter((r) => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return true;
    return r.org_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || (r.contact_name ?? "").toLowerCase().includes(q);
  });

  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [wType, setWType] = useState<AssetType>("brochure");
  const [wTitle, setWTitle] = useState("");
  const [wAudience, setWAudience] = useState("families");
  const [generating, setGenerating] = useState(false);

  const startWizard = () => {
    setWizardOpen(true);
    setStep(1);
    setWType("brochure");
    setWTitle("");
    setWAudience("families");
  };

  const finishWizard = async () => {
    setGenerating(true);
    setTimeout(async () => {
      try {
        await addAsset({ asset_type: wType, title: wTitle || ASSET_LABELS[wType], audience: wAudience });
        toast.success("Asset generated");
        setWizardOpen(false);
      } catch (e: any) {
        toast.error(e.message ?? "Failed to save asset");
      } finally {
        setGenerating(false);
      }
    }, 2200);
  };

  const downloadPdf = (title: string) => {
    toast.loading("Preparing PDF…", { id: title });
    setTimeout(() => toast.success("Downloaded", { id: title }), 1500);
  };

  const openSendPicker = (asset: { id: string; title: string; send_count: number }) => {
    setPickerAsset(asset);
    setPickerSearch("");
    setManualEmail("");
    setPickerOpen(true);
  };

  const sendToRecipient = async (recipient: string) => {
    if (!pickerAsset) return;
    if (!isValidEmail(recipient)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSending(true);
    try {
      await incrementSend(pickerAsset.id, pickerAsset.send_count);
      await logSend({ asset_id: pickerAsset.id, asset_title: pickerAsset.title, recipient });
      toast.success(`Asset sent to ${recipient}`);
      setPickerOpen(false);
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    } finally {
      setSending(false);
    }
  };

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "assets", label: "My Assets", icon: FileText },
    { key: "create", label: "Create", icon: Plus },
    { key: "history", label: "Send History", icon: Send },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "assets" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assetsLoading && <div className="text-sm text-muted-foreground col-span-full">Loading…</div>}
          {!assetsLoading && assets.length === 0 && <div className="text-sm text-muted-foreground col-span-full">No assets yet. Create one on the Create tab.</div>}
          {assets.map((a) => (
            <Card key={a.id}>
              <CardContent className="pt-6 space-y-3">
                <AssetPreview type={a.asset_type as AssetType} />
                <div>
                  <div className="font-semibold text-sm">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{ASSET_LABELS[a.asset_type as AssetType]} · {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => downloadPdf(a.title)}>Download PDF</Button>
                  <Button size="sm" className="flex-1 bg-gold text-gold-foreground hover:bg-gold/90" onClick={() => openSendPicker({ id: a.id, title: a.title, send_count: a.send_count })}>Send{a.send_count > 0 ? ` (${a.send_count})` : ""}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tab === "create" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Create a new asset</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(Object.keys(ASSET_LABELS) as AssetType[]).map((t) => (
                <button key={t} onClick={() => { setWType(t); startWizard(); }} className="p-4 border rounded-lg hover:border-gold text-left">
                  <AssetPreview type={t} />
                  <div className="text-sm font-medium mt-2">{ASSET_LABELS[t]}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "history" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Send history</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {historyLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
              {!historyLoading && history.length === 0 && <div className="text-sm text-muted-foreground">No sends yet.</div>}
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-medium text-sm">{h.asset_title}</div>
                    <div className="text-xs text-muted-foreground">to {h.recipient}</div>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(h.sent_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Asset wizard — Step {step} of 3</DialogTitle></DialogHeader>
          {step === 1 && (
            <div className="space-y-3">
              <Label>Asset type</Label>
              <Select value={wType} onValueChange={(v) => setWType(v as AssetType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(Object.keys(ASSET_LABELS) as AssetType[]).map((t) => <SelectItem key={t} value={t}>{ASSET_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3">
              <Label>Title</Label>
              <Input value={wTitle} onChange={(e) => setWTitle(e.target.value)} placeholder={ASSET_LABELS[wType]} />
              <Label>Audience</Label>
              <Select value={wAudience} onValueChange={setWAudience}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="families">Families</SelectItem>
                  <SelectItem value="referrers">Referral partners</SelectItem>
                  <SelectItem value="general">General community</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-3">
              <div className="text-sm">Preview</div>
              <AssetPreview type={wType} />
              {generating && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> AI is composing your asset…</div>}
            </div>
          )}
          <DialogFooter>
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} disabled={generating}>Back</Button>}
            {step < 3 ? (
              <Button onClick={() => setStep(step + 1)} className="bg-gold text-gold-foreground hover:bg-gold/90">Next</Button>
            ) : (
              <Button onClick={finishWizard} disabled={generating} className="bg-gold text-gold-foreground hover:bg-gold/90">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Generate
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send "{pickerAsset?.title}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Choose a referral partner</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="pl-8" placeholder="Search partners by name or email…" value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} />
              </div>
              <div className="max-h-56 overflow-y-auto border rounded-md divide-y">
                {filteredRecipients.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground text-center">
                    {recipients.length === 0 ? "No referral partners with email yet." : "No matches."}
                  </div>
                )}
                {filteredRecipients.map((r) => (
                  <button
                    key={r.id}
                    disabled={sending}
                    onClick={() => sendToRecipient(r.email)}
                    className="w-full text-left p-3 hover:bg-muted/50 transition flex items-center justify-between gap-2 disabled:opacity-50"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{r.org_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{r.contact_name ? `${r.contact_name} · ` : ""}{r.email}</div>
                    </div>
                    <Send className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Or enter an email manually</Label>
              <div className="flex gap-2">
                <Input type="email" placeholder="name@example.com" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} />
                <Button
                  disabled={sending || !manualEmail}
                  onClick={() => sendToRecipient(manualEmail.trim())}
                  className="bg-gold text-gold-foreground hover:bg-gold/90"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
